// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import Button from '@shared/components/Button';
import DialogTitle from '@shared/components/DialogTitle';

export type ConfirmationProps = {
  className?: string;
}

interface ConfirmationState {
  open: boolean;
  title: React.ReactNode;
  question: React.ReactNode;
  callback: (isConfirmed?: boolean) => void;
}

let showConfirmationFn;

export function showConfirmation({ title, question }: Partial<ConfirmationState>, callback: (isConfirmed: boolean) => void): void {
  showConfirmationFn({ title, question }, callback);
}

class Confirmation extends React.Component<ConfirmationProps, ConfirmationState> {
  readonly state = {
    open: false,
    title: '',
    question: '',
    callback: (isConfirmed?: boolean): void => { }
  };

  componentDidMount() {
    showConfirmationFn = this.show;
  }

  private show = ({ title, question }: Partial<ConfirmationState>, callback: (isConfirmed: boolean) => void): void => {
    this.setState({ title, question, callback, open: true });
  }

  private terminate = () => {
    this.state.callback(false);
    this.hide();
  }

  private hide = () => {
    this.setState({ open: false });
  }

  private handleYes = () => {
    this.state.callback(true);
    this.hide();
  }

  render() {
    const classes = withStyles.getClasses(this.props);
    const { open, title, question } = this.state;

    return (
      <Dialog
        open={open}
        classes={{ paper: classes.root }}
      >
        <DialogTitle
          heading={title}
          onClose={this.terminate}
        />
        <DialogContent className={classes.content}>{question}</DialogContent>
        <DialogActions classes={{ root: classes.actions }}>
          <Button
            className={classes.actionBtn}
            text="Yes"
            color="primary"
            variant="contained"
            onClick={this.handleYes}
          />
          <Button
            className={classes.actionBtn}
            text="No"
            color="primary"
            variant="contained"
            onClick={this.terminate}
          />
        </DialogActions>
      </Dialog>
    );
  }
}

const ConfirmationStyled = withStyles(
  Confirmation, 
  (theme, props) => ({
    "root": {
      maxWidth: 'none',
      minWidth: 552
    },
    "content": {
      padding: theme.spacing(5, 5, 8),
      fontSize: 15,
    },
    "actions": {
      padding: theme.spacing(4, 5),
      backgroundColor: theme.palette.colors.greyLight5,
      justifyContent: 'flex-start',
    },
    "actionBtn": {
      fontSize: 16,
    }
  })
);

export default ConfirmationStyled;